/**
 * DevTools — Floating panel for local development.
 * Only renders when VITE_DEV_TOOLS=true or in dev mode.
 */
import * as React from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useUser } from '@clerk/tanstack-react-start';
import { api } from '@/convex';
import { OrgContext, ACTIVE_ORG_STORAGE_KEY } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import { btnStyle } from './devToolsStyles';
import { DevSeedTools } from './DevSeedTools';

export function DevTools() {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    const isDev =
      import.meta.env.DEV || import.meta.env.VITE_DEV_TOOLS === 'true';
    if (isDev) setMounted(true);
  }, []);

  if (!mounted) return null;

  return <DevToolsPanel />;
}

function DevToolsPanel() {
  const orgCtx = React.useContext(OrgContext);
  const hasOrg = orgCtx !== null && !orgCtx.isLoading;

  if (hasOrg) {
    return <DevToolsInner />;
  }
  return <DevToolsMinimal />;
}

/**
 * Rendered when OrgContext is available — can safely
 * call useProjectContext (which calls useOrgContext internally).
 */
function DevToolsInner() {
  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState('');
  const [previewHtml, setPreviewHtml] = React.useState<string | null>(null);

  const orgCtx = React.useContext(OrgContext)!;
  const orgId = orgCtx.orgId;
  const { selectedProjectId: projectId } = useProjectContext();

  const restartOnboardingFromStart = useMutation(
    api.devReset.restartOnboardingFromStart,
  );
  const restartOnboardingAtPrimitives = useMutation(
    api.devReset.restartOnboardingAtPrimitives,
  );
  const grantTrial = useMutation(api.devReset.grantTrialSubscription);
  const captureSnapshot = useMutation(api.findingSnapshots.triggerCapture);
  const sendTestDigest = useMutation(api.digestHelpers.sendTestDigest);

  const previewResult = useQuery(
    api.digestHelpers.previewDigestHtml,
    orgId && projectId ? { orgId, projectId } : 'skip',
  );

  async function handleRestartOnboarding() {
    if (
      !window.confirm(
        'This will restart onboarding for your current workspace while keeping GitHub auth, repo bindings, and scan cache. Continue?',
      )
    )
      return;

    setStatus('Restarting onboarding...');
    try {
      const result = await restartOnboardingFromStart();
      setStatus(
        result.selectedRepo
          ? `Restarting with ${result.selectedRepo} preserved...`
          : 'Restarting from first step...',
      );
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      setTimeout(() => {
        window.location.href = '/onboarding';
      }, 500);
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  async function handleOpenPrimitivesFromCache() {
    if (
      !window.confirm(
        'This will open onboarding at primitive selection using the current cached scan. Continue?',
      )
    )
      return;

    setStatus('Opening cached primitive step...');
    try {
      const result = await restartOnboardingAtPrimitives();
      setStatus(
        result.openedPrimitives
          ? `Opening primitives for ${result.selectedRepo}...`
          : 'No cached workspace found; restarting from first step...',
      );
      if (result.orgId) {
        localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, result.orgId);
      } else {
        localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      }
      setTimeout(() => {
        window.location.href = '/onboarding';
      }, 500);
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }


  async function handleSnapshot() {
    if (!projectId) {
      setStatus('No repository selected');
      return;
    }
    setStatus('Capturing snapshot...');
    try {
      await captureSnapshot({ projectId });
      setStatus('Snapshot captured');
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  async function handleSkipPlan() {
    if (!orgId) {
      setStatus('No org selected');
      return;
    }
    setStatus('Granting trial subscription...');
    try {
      await grantTrial({ orgId });
      setStatus('Trial granted — redirecting...');
      window.location.href = '/onboarding?checkout=success';
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  async function handleSendDigest() {
    if (!orgId || !projectId) {
      setStatus('No org/project selected');
      return;
    }
    setStatus('Sending test digest...');
    try {
      const result = await sendTestDigest({
        orgId,
        projectId,
      });
      setStatus(`Digest sent to ${result.sentTo}`);
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  function handlePreview() {
    if (previewResult) {
      setPreviewHtml(previewResult);
    } else {
      setStatus('No preview available — need org + project');
    }
  }

  if (!open) {
    return <DevToolsFab onClick={() => setOpen(true)} />;
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: 12,
          right: 12,
          zIndex: 9999,
          width: 280,
          padding: 16,
          background: '#1a1a2e',
          border: '1px solid #333',
          borderRadius: 10,
          fontFamily: 'monospace',
          fontSize: 12,
          color: '#ccc',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 600, color: '#fff' }}>Dev Tools</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            x
          </button>
        </div>

        <DevSeedTools />

        <button
          type="button"
          onClick={handleRestartOnboarding}
          style={btnStyle}
        >
          Restart onboarding, keep workspace/cache
        </button>

        <button
          type="button"
          onClick={handleOpenPrimitivesFromCache}
          style={btnStyle}
        >
          Open primitives from cached scan
        </button>

        <div
          style={{
            borderTop: '1px solid #333',
            paddingTop: 8,
            fontSize: 10,
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Onboarding
        </div>

        <button
          type="button"
          onClick={handleSkipPlan}
          style={btnStyle}
          disabled={!orgId}
        >
          Skip plan → grant trial
        </button>

        <div
          style={{
            borderTop: '1px solid #333',
            paddingTop: 8,
            fontSize: 10,
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Findings &amp; Digests
        </div>

        <button
          type="button"
          onClick={handleSnapshot}
          style={btnStyle}
          disabled={!projectId}
        >
          Capture snapshot now
        </button>

        <button
          type="button"
          onClick={handleSendDigest}
          style={btnStyle}
          disabled={!orgId || !projectId}
        >
          Send test digest to me
        </button>

        <button
          type="button"
          onClick={handlePreview}
          style={btnStyle}
          disabled={!previewResult}
        >
          Preview digest email
        </button>

        {status && <div style={{ fontSize: 11, color: '#888' }}>{status}</div>}
      </div>

      {previewHtml && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setPreviewHtml(null)}
        >
          <div
            style={{
              width: '640px',
              maxHeight: '90vh',
              background: '#fff',
              borderRadius: 8,
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '8px 16px',
                background: '#18181b',
                color: '#fff',
                fontSize: 12,
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Digest Email Preview</span>
              <button
                type="button"
                onClick={() => setPreviewHtml(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                x
              </button>
            </div>
            <iframe
              srcDoc={previewHtml}
              title="Digest Preview"
              style={{
                width: '100%',
                height: '600px',
                border: 'none',
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Rendered outside OrgProvider (onboarding, waitlist, etc.)
 * Only shows the reset button.
 */
function DevToolsMinimal() {
  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState('');
  const restartOnboardingFromStart = useMutation(
    api.devReset.restartOnboardingFromStart,
  );
  const restartOnboardingAtPrimitives = useMutation(
    api.devReset.restartOnboardingAtPrimitives,
  );
  const grantTrial = useMutation(api.devReset.grantTrialSubscription);

  const { user } = useUser();
  const clerkUserId = user?.id;
  const onboarding = useQuery(
    api.onboarding.getByUser,
    clerkUserId ? { clerkUserId } : 'skip',
  );
  const onboardingOrgId = onboarding?.orgId;

  async function handleRestartOnboarding() {
    if (
      !window.confirm(
        'This will restart onboarding for your current workspace while keeping GitHub auth, repo bindings, and scan cache. Continue?',
      )
    )
      return;
    setStatus('Restarting onboarding...');
    try {
      const result = await restartOnboardingFromStart();
      setStatus(
        result.selectedRepo
          ? `Restarting with ${result.selectedRepo} preserved...`
          : 'Restarting from first step...',
      );
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      setTimeout(() => {
        window.location.href = '/onboarding';
      }, 500);
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  async function handleOpenPrimitivesFromCache() {
    if (
      !window.confirm(
        'This will open onboarding at primitive selection using the current cached scan. Continue?',
      )
    )
      return;
    setStatus('Opening cached primitive step...');
    try {
      const result = await restartOnboardingAtPrimitives();
      setStatus(
        result.openedPrimitives
          ? `Opening primitives for ${result.selectedRepo}...`
          : 'No cached workspace found; restarting from first step...',
      );
      if (result.orgId) {
        localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, result.orgId);
      } else {
        localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      }
      setTimeout(() => {
        window.location.href = '/onboarding';
      }, 500);
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  async function handleSkipPlan() {
    if (!onboardingOrgId) {
      setStatus('No onboarding org yet');
      return;
    }
    setStatus('Granting trial subscription...');
    try {
      await grantTrial({ orgId: onboardingOrgId });
      setStatus('Trial granted — redirecting...');
      window.location.href = '/onboarding?checkout=success';
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  if (!open) {
    return <DevToolsFab onClick={() => setOpen(true)} />;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 48,
        right: 12,
        zIndex: 9999,
        width: 280,
        padding: 16,
        background: '#1a1a2e',
        border: '1px solid #333',
        borderRadius: 10,
        fontFamily: 'monospace',
        fontSize: 12,
        color: '#ccc',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontWeight: 600, color: '#fff' }}>Dev Tools</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#666',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          x
        </button>
      </div>
      <DevSeedTools />
      <button type="button" onClick={handleRestartOnboarding} style={btnStyle}>
        Restart onboarding, keep workspace/cache
      </button>
      <button
        type="button"
        onClick={handleOpenPrimitivesFromCache}
        style={btnStyle}
      >
        Open primitives from cached scan
      </button>
      <button
        type="button"
        onClick={handleSkipPlan}
        style={btnStyle}
        disabled={!onboardingOrgId}
      >
        Skip plan → grant trial
      </button>
      {status && <div style={{ fontSize: 11, color: '#888' }}>{status}</div>}
    </div>
  );
}

function DevToolsFab({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'fixed',
        bottom: 48,
        right: 12,
        zIndex: 9999,
        padding: '4px 10px',
        fontSize: 11,
        fontFamily: 'monospace',
        background: '#1a1a2e',
        color: '#888',
        border: '1px solid #333',
        borderRadius: 6,
        cursor: 'pointer',
        opacity: 0.6,
      }}
    >
      DEV
    </button>
  );
}
